import React, { useState } from "react";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Pagination, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import {
  useDeleteSyncScheduleMutation,
  useDisableSyncScheduleMutation,
  useEnableSyncScheduleMutation,
  useGetSyncSchedulesQuery,
  useLazyGetSyncScheduleQuery,
} from "state/rtkQuery/sync/syncQueries";

import { PAGE_REFRESH } from "./historyFilters";
import ScheduleDrawer from "./ScheduleDrawer";
import { formatDateTime, normalizeApiError, parseBackendTime, scheduleWhen } from "./syncFormat";
import { headCellSx, Panel, Pill, QueueName, TaskName } from "./SyncParts";

const PAGE_SIZES = [25, 50, 100];
const FILTERS = [
  { value: "all", label: "All" },
  { value: "true", label: "Enabled" },
  { value: "false", label: "Disabled" },
];
const FILTER_TITLE = { all: "All schedules", true: "Enabled schedules", false: "Disabled schedules" };

// The next run is the service's ESTIMATE (it is not the scheduler acknowledging anything). Overdue estimates are shown
// as overdue, never quietly replaced with "now".
export const NextRun = ({ schedule, now = Date.now() }) => {
  if (schedule.enabled === false) return <span style={{ color: "#94a3b8" }}>Paused</span>;
  const time = parseBackendTime(schedule.next_run_at);
  if (!time) {
    return (
      <Tooltip title={schedule.next_run_unavailable_reason || "The service couldn't estimate it"}>
        <span style={{ color: "#94a3b8" }}>Not available</span>
      </Tooltip>
    );
  }
  const overdue = time.valueOf() < now;
  return (
    <Tooltip title="An estimate from the schedule, not a confirmation from the scheduler.">
      <span>
        {formatDateTime(schedule.next_run_at)} {overdue && <Pill tone="warn">overdue</Pill>}
      </span>
    </Tooltip>
  );
};

const SchedulesTab = ({ onNotify }) => {
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);
  const { data, isLoading, isFetching, isError, error, refetch } = useGetSyncSchedulesQuery({ enabled: filter, skip: (page - 1) * size, limit: size }, PAGE_REFRESH);
  const [disableSchedule] = useDisableSyncScheduleMutation();
  const [enableSchedule] = useEnableSyncScheduleMutation();
  const [deleteSchedule, { isLoading: deleting }] = useDeleteSyncScheduleMutation();
  const [fetchSchedule] = useLazyGetSyncScheduleQuery();

  const schedules = data?.items || [];
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / size));
  const [drawerName, setDrawerName] = useState(undefined); // undefined closed, null create, string edit
  const [confirm, setConfirm] = useState(null); // { kind: "disable"|"enable"|"delete", name }
  const [busyName, setBusyName] = useState(null);
  const [lookupName, setLookupName] = useState("");
  const [lookupError, setLookupError] = useState("");

  // An enable/disable can fail with 404 when the schedule is already in the wanted state: show what is really stored.
  const explainStateError = async (name, normalized) => {
    const response = await fetchSchedule({ name }, false);
    if (response.data) onNotify(`${normalized.message} It is currently ${response.data.enabled ? "enabled" : "paused"}.`, "warning");
    else onNotify(normalized.message, "error");
  };

  const runConfirmed = async () => {
    const { kind, name } = confirm;
    setBusyName(name);
    try {
      if (kind === "disable") {
        await disableSchedule({ name }).unwrap();
        onNotify(`Paused ${name}. The scheduler stops using it within about 5 minutes.`, "success");
      } else if (kind === "enable") {
        await enableSchedule({ name }).unwrap();
        onNotify(`Enabled ${name}. The scheduler picks it up within about 5 minutes.`, "success");
      } else {
        await deleteSchedule({ name }).unwrap();
        onNotify(`Deleted ${name}. Tasks it already queued still run.`, "success");
      }
    } catch (err) {
      const normalized = normalizeApiError(err);
      if (kind === "delete" || normalized.uncertain) onNotify(normalized.message, normalized.uncertain ? "warning" : "error");
      else await explainStateError(name, normalized);
    } finally {
      setBusyName(null);
      setConfirm(null);
    }
  };

  const lookup = async () => {
    const name = lookupName.trim();
    if (!name) return;
    setLookupError("");
    const response = await fetchSchedule({ name }, false);
    if (response.isError) setLookupError(response.error?.status === 404 ? `No schedule named “${name}”.` : normalizeApiError(response.error).message);
    else setDrawerName(response.data.name);
  };

  return (
    <>
      <Panel
        title={FILTER_TITLE[filter]}
        subtitle={`${total.toLocaleString("en-US")} · times are UTC · changes reach the scheduler within about 5 minutes`}
        actions={
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <ToggleButtonGroup exclusive size="small" value={filter} onChange={(_, value) => { if (value) { setFilter(value); setPage(1); } }} aria-label="Schedule filter">
              {FILTERS.map((option) => <ToggleButton key={option.value} value={option.value} sx={{ textTransform: "none", px: 1.5 }}>{option.label}</ToggleButton>)}
            </ToggleButtonGroup>
            <TextField size="small" placeholder="Open a schedule by exact name" value={lookupName} onChange={(event) => setLookupName(event.target.value)} sx={{ width: 260 }} />
            <Button variant="outlined" onClick={lookup} disabled={!lookupName.trim()} sx={{ textTransform: "none" }}>Open</Button>
            <Button variant="contained" onClick={() => setDrawerName(null)} sx={{ background: "#86BC25", textTransform: "none", fontWeight: 700, boxShadow: "none", "&:hover": { background: "#6a9a1e", boxShadow: "none" } }}>＋ New schedule</Button>
          </Box>
        }
      >
        {lookupError && <Alert severity="info" sx={{ m: 2 }}>{lookupError}</Alert>}
        {isError ? (
          <Alert severity="error" sx={{ m: 2 }} action={<Button color="inherit" size="small" onClick={refetch}>Retry</Button>}>
            {error?.status === 403 ? "You don't have permission to read schedules." : `Couldn't load the schedules. ${normalizeApiError(error).message}`}
          </Alert>
        ) : isLoading ? (
          <Typography sx={{ p: 4, textAlign: "center", color: "#94a3b8" }}>Loading schedules…</Typography>
        ) : schedules.length === 0 ? (
          <Typography sx={{ p: 4, textAlign: "center", color: "#94a3b8" }}>{filter === "all" ? "No schedules yet." : `No ${filter === "true" ? "enabled" : "disabled"} schedules.`}</Typography>
        ) : (
          <>
            <Table size="small" sx={{ opacity: isFetching ? 0.6 : 1 }}>
              <TableHead>
                <TableRow>
                  {["Name", "Task", "When (UTC)", "Queue", "Estimated next run", "Last run", "Runs", "Enabled", ""].map((label) => (
                    <TableCell key={label} sx={headCellSx}>{label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {schedules.map((schedule) => {
                  const enabled = schedule.enabled !== false;
                  return (
                    <TableRow key={schedule.name} data-testid="schedule-row" sx={{ opacity: enabled ? 1 : 0.7 }}>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {schedule.name} {!enabled && <Pill tone="neutral">Paused</Pill>}
                        {schedule.deduplication?.enabled && <Typography sx={{ color: "#64748b", fontSize: 12 }}>skips if it ran recently</Typography>}
                      </TableCell>
                      <TableCell><TaskName name={schedule.task} /></TableCell>
                      <TableCell>
                        <Pill tone={schedule.schedule_type === "interval" ? "purple" : "blue"}>{schedule.schedule_type === "interval" ? "interval" : "cron"}</Pill>{" "}
                        <span style={{ fontSize: 12.5 }}>{scheduleWhen(schedule)}</span>
                      </TableCell>
                      <TableCell sx={{ fontSize: 12.5 }}><QueueName queue={schedule.queue} /></TableCell>
                      <TableCell sx={{ fontSize: 12.5 }} data-testid="next-run"><NextRun schedule={schedule} /></TableCell>
                      <TableCell sx={{ fontSize: 12.5 }}>{schedule.last_run_at ? formatDateTime(schedule.last_run_at) : "Never"}</TableCell>
                      <TableCell sx={{ fontSize: 12.5 }}>{Number(schedule.total_run_count ?? 0).toLocaleString("en-US")}</TableCell>
                      <TableCell>
                        <Switch
                          size="small"
                          checked={enabled}
                          disabled={busyName === schedule.name}
                          onChange={() => setConfirm({ kind: enabled ? "disable" : "enable", name: schedule.name })}
                          inputProps={{ "aria-label": `${enabled ? "Pause" : "Enable"} ${schedule.name}` }}
                          color="success"
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Button size="small" variant="outlined" onClick={() => setDrawerName(schedule.name)} sx={{ textTransform: "none", mr: 1 }}>Edit</Button>
                        <Button size="small" variant="outlined" color="error" onClick={() => setConfirm({ kind: "delete", name: schedule.name })} sx={{ textTransform: "none" }}>Delete</Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {total > size && (
              <Box sx={{ p: "12px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #f1f5f9" }}>
                <Typography sx={{ color: "#64748b", fontSize: 12.5 }}>{(page - 1) * size + 1}–{(page - 1) * size + schedules.length} of {total.toLocaleString("en-US")}</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <TextField select size="small" label="Rows" value={size} onChange={(event) => { setSize(Number(event.target.value)); setPage(1); }} sx={{ width: 90 }}>
                    {PAGE_SIZES.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                  </TextField>
                  <Pagination count={pages} page={Math.min(page, pages)} onChange={(_, value) => setPage(value)} shape="rounded" size="small" />
                </Box>
              </Box>
            )}
          </>
        )}
      </Panel>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", color: "#64748b", fontSize: 12, mt: 2 }}>
        <span><Pill tone="purple">interval</Pill> every N seconds, minutes, hours or days</span>
        <span><Pill tone="blue">cron</Pill> minute hour day-of-month month day-of-week</span>
        <span>"Estimated next run" is the schedule's own estimate: it can be overdue and is not a scheduler confirmation.</span>
      </Box>

      {drawerName !== undefined && (
        <ScheduleDrawer
          name={drawerName}
          onClose={() => setDrawerName(undefined)}
          onSaved={(message) => {
            setDrawerName(undefined);
            onNotify(message, "success");
          }}
        />
      )}

      <Dialog open={Boolean(confirm)} onClose={() => !busyName && setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {confirm?.kind === "delete" ? "Delete" : confirm?.kind === "enable" ? "Enable" : "Pause"} {confirm?.name}?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13.5 }}>
            {confirm?.kind === "delete" && "This removes the schedule permanently. Tasks it already queued will still run."}
            {confirm?.kind === "enable" && "It will start running again on its schedule."}
            {confirm?.kind === "disable" && "It stops running on its schedule. Tasks already queued are not cancelled."}
          </Typography>
          <Typography sx={{ color: "#64748b", fontSize: 12.5, mt: 1 }}>The scheduler picks the change up within about 5 minutes.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: "8px 24px 18px" }}>
          <Button onClick={() => setConfirm(null)} disabled={Boolean(busyName) || deleting}>Cancel</Button>
          <Button variant="contained" color={confirm?.kind === "delete" ? "error" : "primary"} onClick={runConfirmed} disabled={Boolean(busyName) || deleting} sx={{ textTransform: "none", fontWeight: 700 }}>
            {confirm?.kind === "delete" ? "Delete schedule" : confirm?.kind === "enable" ? "Enable" : "Pause"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SchedulesTab;
